// ============================================================
// HOOKED ENGINE — SPRITE          [Tier 1 harvest, verbatim]
// One component. Used by every zone, every NPC, every player.
//
// Features:
//   - key={src} forces remount when src changes — clears error cache
//   - prevSrc useEffect resets error state on src change
//   - onError fallback to colored div — never a broken image
//   - imageRendering: pixelated — consistent pixel art look
//   - Background image variant for zone backgrounds
// ============================================================

import { useState, useEffect, useRef } from "react";

// ── Standard sprite — player, NPC, companion, card art ────────
export function Sprite({
  src,
  alt = "",
  width,
  height,
  size,           // shorthand — sets both width and height
  style = {},
  fallbackColor = "#d9a83f",
  fallbackContent = null,
  onError: onErrorProp,
}) {
  const [err, setErr] = useState(false);
  const prevSrc = useRef(src);

  useEffect(() => {
    if (prevSrc.current !== src) {
      prevSrc.current = src;
      setErr(false);
    }
  }, [src]);

  const w = width  || size || "100%";
  const h = height || size || "100%";

  if (!src || err) {
    return (
      <div style={{
        width: w, height: h,
        background: fallbackColor,
        borderRadius: 4,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", flexShrink: 0,
        ...style,
      }}>
        {fallbackContent}
      </div>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt={alt}
      onError={() => { setErr(true); onErrorProp?.(); }}
      style={{
        width: w, height: h,
        imageRendering: "pixelated",
        display: "block",
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

// ── Background image — zone backgrounds, covers the map ───────
// Hides silently on error, parent background-color shows through
export function BgImage({ src, style = {}, fallbackBg = "linear-gradient(180deg,#010208,#071226,#051826)" }) {
  return (
    <img
      key={src}
      src={src}
      alt=""
      draggable="false"
      onError={e => {
        e.target.style.display = "none";
        if (e.target.parentNode) e.target.parentNode.style.background = fallbackBg;
      }}
      style={{
        position: "absolute", inset: 0,
        width: "100%", height: "100%",
        objectFit: "cover",
        objectPosition: "center top",
        zIndex: 0,
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}

// ── Inline image — faction reps, NPCs with specific sizing ────
export function ZoneImage({ src, alt = "", style = {}, onError }) {
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      onError={e => { e.target.style.display = "none"; onError?.(); }}
      style={{
        width: "100%", height: "100%",
        objectFit: "contain",
        imageRendering: "pixelated",
        ...style,
      }}
    />
  );
}
